import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import StyledDataGrid from "../../../components/StyledDataGrid";
import {
  fetchFollowUpDashboard,
  fetchFollowUpEnrollmentPreview,
  enrollFollowUp,
} from "../../../utils/api";
import { getStatusChipStyle, BASE_CHIP_SX } from "../../../constants/statusColors";
import { formatShortDateKey } from "../../../utils/appDateTime";
import FollowUpFilters from "./FollowUpFilters";
import { patientIdOf } from "../email-center/EmailPatientPicker";

const formatDateOnly = (value) => {
  if (!value) return "N/A";
  return formatShortDateKey(value, String(value));
};

const getStatusColor = (status, isH0 = false) => {
  if (isH0) return "#4caf50";
  switch (status) {
    case "COMPLETED":
      return "#4caf50";
    case "MISSED":
      return "#f44336";
    case "READY":
    case "IN_PROGRESS":
      return "#ff9800";
    case "PLANNED":
      return "#9e9e9e";
    default:
      return "#e0e0e0";
  }
};

const CompleteDashboardTab = ({ setError }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollPreview, setEnrollPreview] = useState(null);
  const [enrollSubmitting, setEnrollSubmitting] = useState(false);
  const [enrollBatchId, setEnrollBatchId] = useState("");

  const selectedPatientId = patientIdOf(selectedPatient);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page,
        size: paginationModel.pageSize,
      };
      if (selectedPatientId) params.patientId = selectedPatientId;
      if (selectedBatchId) params.batchId = selectedBatchId;

      const data = await fetchFollowUpDashboard(params);
      const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      setRows(content.map((row, index) => ({
        ...row,
        id: row.batchId || index,
        patientName: `${row.firstName || ""} ${row.lastName || ""}`.trim() || "N/A",
        accountNumber: row.patientAccountNumber || "N/A",
        barcode: row.batchId || "N/A",
        baselineDate: row.baselineCollectedDateTime,
        isEnrolled: row.alreadyEnrolled,
        milestones: row.milestones || [],
      })));
      setTotalRows(data?.totalElements || content.length);
    } catch (err) {
      console.error("Failed to load complete dashboard:", err);
      setError("Failed to load complete dashboard.");
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, selectedBatchId, selectedPatientId, setError]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const applyBatchFilter = useCallback((nextBatchId) => {
    setSelectedBatchId(nextBatchId || "");
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const applyPatientFilter = useCallback((patient) => {
    setSelectedPatient(patient || null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedBatchId("");
    setSelectedPatient(null);
    setPaginationModel((current) => ({ ...current, page: 0 }));
  }, []);

  const handleEnrollClick = useCallback(async (batchId) => {
    setEnrollBatchId(batchId);
    setEnrollPreview(null);
    setEnrollModalOpen(true);
    try {
      const data = await fetchFollowUpEnrollmentPreview(batchId);
      setEnrollPreview(data);
    } catch (err) {
      console.error("Failed to fetch enrollment preview", err);
      setError("Failed to load enrollment preview.");
      setEnrollModalOpen(false);
    }
  }, [setError]);

  const handleConfirmEnroll = async () => {
    setEnrollSubmitting(true);
    try {
      await enrollFollowUp({
        batchId: enrollBatchId,
        suggestedReadyMilestoneNumber: enrollPreview?.suggestedCurrentMilestone || undefined,
      });
      setEnrollModalOpen(false);
      await loadDashboard();
    } catch (err) {
      console.error("Failed to enroll batch", err);
      setError("Failed to enroll batch in follow-up plan.");
    } finally {
      setEnrollSubmitting(false);
    }
  };

  const columns = useMemo(() => [
    { field: "patientName", headerName: "Participant", flex: 0.8 },
    { field: "accountNumber", headerName: "MRN", flex: 0.6 },
    { field: "barcode", headerName: "Barcode", flex: 0.6 },
    {
      field: "baselineDate",
      headerName: "Baseline Date",
      flex: 0.6,
      renderCell: (params) => formatDateOnly(params.value),
    },
    {
      field: "milestones",
      headerName: "Milestones",
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        const milestones = params.value || [];
        const indices = [0, ...Array.from({ length: 10 }, (_, index) => index + 1)];

        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 1, height: "100%", alignItems: "center" }}>
            {indices.map((milestoneNumber) => {
              let status = null;
              let dueDate = null;
              let tooltipContent = null;

              if (milestoneNumber === 0) {
                status = "COMPLETED";
                tooltipContent = "Baseline";
              } else {
                const milestone = milestones.find((item) => item.milestoneNumber === milestoneNumber);
                if (milestone) {
                  status = milestone.followUpStatus;
                  tooltipContent = params.row.isEnrolled ? (milestone.followUpStatus || "PENDING") : "Not Enrolled";
                  dueDate = milestone.scheduledDueDate;
                } else {
                  tooltipContent = "Not available";
                }
              }

              const color = getStatusColor(status, milestoneNumber === 0);
              const displayTooltip = dueDate
                ? `${tooltipContent} (${formatDateOnly(dueDate)})`
                : tooltipContent;

              return (
                <Tooltip key={milestoneNumber} title={displayTooltip}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "11px",
                      fontWeight: "bold",
                      border: "1px solid rgba(0,0,0,0.1)",
                      cursor: "default",
                    }}
                  >
                    {milestoneNumber}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 0.8,
      sortable: false,
      align: "center",
      renderCell: (params) => {
        if (!params.row.isEnrolled) {
          return (
            <Button
              variant="contained"
              onClick={() => handleEnrollClick(params.row.batchId)}
              sx={{
                width: "100px",
                height: "32px",
                fontSize: "13px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                borderRadius: "10px",
              }}
            >
              ENROLL
            </Button>
          );
        }
        return (
          <Chip
            label="ENROLLED"
            size="small"
            clickable={false}
            sx={{
              ...BASE_CHIP_SX,
              ...getStatusChipStyle("COMPLETE"),
              width: "100px",
              cursor: "default",
            }}
          />
        );
      },
    },
  ], [handleEnrollClick]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex", flexDirection: "column" }}>
      <FollowUpFilters
        batchId={selectedBatchId}
        onBatchIdChange={applyBatchFilter}
        selectedPatient={selectedPatient}
        onSelectedPatientChange={applyPatientFilter}
        onClear={clearFilters}
      />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          getRowId={(row) => row.id}
          disableSelectionOnClick
          sx={{ height: "100%" }}
        />
      </Box>

      <Dialog
        open={enrollModalOpen}
        onClose={() => !enrollSubmitting && setEnrollModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Brand Headers', sans-serif", fontWeight: 700, color: "#500000" }}>
          Enroll Batch in Follow-up Plan
        </DialogTitle>
        <DialogContent dividers>
          {!enrollPreview ? (
            <Typography>Loading preview...</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Barcode</Typography>
                <Typography>{enrollPreview.batchId || enrollBatchId || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Baseline</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography>{enrollPreview.rootSampleId || "N/A"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collected: {formatDateOnly(enrollPreview.baselineCollectedDateTime)}
                  </Typography>
                </Stack>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">Projected Milestones</Typography>
              <Box sx={{ border: "1px solid #eee", borderRadius: 1 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ p: 1, backgroundColor: "#f9f9f9", borderBottom: "1px solid #eee" }}
                >
                  <Typography variant="caption" sx={{ fontWeight: "bold", flex: 1, textAlign: "center" }}>
                    Milestone
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: "bold", flex: 1, textAlign: "center" }}>
                    Scheduled Date
                  </Typography>
                </Stack>
                <Box sx={{ maxHeight: 200, overflowY: "auto", p: 1 }}>
                  {(enrollPreview.milestones || []).map((milestone, index) => {
                    const isOverdue = (enrollPreview.overdueMilestones || []).includes(milestone.milestoneNumber);
                    return (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        key={index}
                        sx={{ py: 0.5, borderBottom: "1px solid #f5f5f5" }}
                      >
                        <Typography variant="body2" sx={{ flex: 1, textAlign: "center" }}>
                          {milestone.milestoneNumber}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={isOverdue ? "error" : "inherit"}
                          sx={{ flex: 1, textAlign: "center" }}
                        >
                          {formatDateOnly(milestone.scheduledDueDate)}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
              {(enrollPreview.overdueMilestones?.length > 0) && (
                <Alert severity="warning" icon={<InfoIcon />}>
                  There are {enrollPreview.overdueMilestones.length} overdue milestones. Enrollment
                  will mark milestones up to the current milestone as READY and later milestones as
                  PLANNED.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollModalOpen(false)} disabled={enrollSubmitting}>Cancel</Button>
          <Button onClick={handleConfirmEnroll} variant="contained" disabled={!enrollPreview || enrollSubmitting}>
            Confirm Enrollment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompleteDashboardTab;
